import * as React from "react";
// eslint-disable-next-line import/no-extraneous-dependencies
import { matchPath } from 'react-router';
import type { IMegaMenuBloc, IMegaMenuData, IMegaMenuEntry, IMegaMenuFavorite, IMegaMenuItem } from './definitions';
import { DynLabel } from "@@commons";
import { MegaMenuPanel } from "./megamenupanel";
import type { IMegaMenuRootProps } from "./megamenuroot";
import { MegaMenuPanelLink } from "./megamenupanel.item";
import { Tooltip } from "@inwink/modals/tooltip";
import { AppLabel } from "@@services/i18nservice";

export interface IMegaMenuRootEntryControlProps extends IMegaMenuRootProps {
    key?: string;
    dataManager: IMegaMenuData;
    entry: IMegaMenuEntry;
    expanded: boolean;
    onShowPanel: (entry: IMegaMenuEntry) => void;
    onToggleFavorites: (items: IMegaMenuFavorite[]) => void;
}

interface IBlocFavoriteProj {
    bloc: IMegaMenuBloc;
    favorites: IMegaMenuItem[];
}

export class MegaMenuRootEntryControl extends React.Component<IMegaMenuRootEntryControlProps, any> {
    hasItemMatch() {
        let hasMatch = false;
        this.props.entry.sections.forEach((s) => {
            s.blocs.forEach((b) => {
                b.items.forEach((it) => {
                    if (it.link) {
                        const linkTarget = it.link
                            .replace('{customerid}', this.props.match.params.customerid)
                            .replace('{tenantid}', this.props.match.params.tenantid)
                            .replace('{eventid}', this.props.match.params.eventid)
                            .replace('{communityid}', this.props.match.params.communityid);
                        const match = matchPath(this.props.location.pathname, {
                            path: linkTarget,
                            exact: true
                        });
                        if (match) {
                            hasMatch = true;
                        }
                    }
                });
            });
        });
        return hasMatch;
    }

    renderPanel(p) {
        return <MegaMenuPanel {...this.props} {...p} />;
    }

    renderItem() {
        return <>
            {this.props.entry.icon ? <div className="icon"><i className={this.props.entry.icon} /></div> : null}
            {this.props.entry.title ? <AppLabel className="label" i18n={this.props.entry.title} /> : null}
            {this.props.entry.label ? <DynLabel className="label" i18n={this.props.entry.label} /> : null}
            {this.props.entry.badgeText ? (
                <span className="item-badge">
                    <AppLabel i18n={this.props.entry.badgeText} />
                </span>
            ) : null}
        </>;
    }

    renderFavItems() {
        const entry = this.props.entry;
        const favorites: IBlocFavoriteProj[] = [];
        entry.sections.forEach((s) => {
            if (s.blocs?.length) {
                s.blocs.forEach((b) => {
                    const bfavorites = [];
                    b.items.forEach((it) => {
                        if (it.favorite) {
                            bfavorites.push(it);
                        }
                    });
    
                    if (bfavorites.length > 0) {
                        favorites.push({
                            bloc: b,
                            favorites: bfavorites
                        });
                    }
                });
            } else if (s.component) {
                const cFavorites = [];
                this.props.menuContext?.favorites?.favorites?.forEach((fav) => {
                    if (fav.menuKey === entry.key)
                        cFavorites.push(fav);
                });

                favorites.push({
                    bloc: { key: entry.key, items: []},
                    favorites: cFavorites
                });
            }
        });
        return <div className="megamenu-entry-items">
            {favorites.map((bit, bidx) => {
                const withLabel = false; // bit.bloc.title || bit.bloc.label;
                return <div className={"megamenu-enty-items-bloc" + (withLabel ? ' withlabel' : '')}
                    key={bit.bloc.key || "itemb" + bidx}>
                    {/* {bit.bloc.title ? <AppLabel className="label" i18n={bit.bloc.title} /> : null}
                    {bit.bloc.label ? <DynLabel className="label" i18n={bit.bloc.label} /> : null} */}
                    {bit.favorites.map((it, idx) => {
                        return <MegaMenuPanelLink
                            {...this.props}
                            key={it.key || "item" + idx}
                            item={it}
                            useFavTitle
                            className="megamenu-entry-favitem"
                        />;
                    })}
                </div>;
            })}
        </div>;
    }

    renderMobilePanel = (arg: React.MouseEvent) => {
        arg.preventDefault();
        arg.stopPropagation();
        this.props.onShowPanel(this.props.entry);
    };

    renderMobile() {
        const hasMatch = this.hasItemMatch();
        let visualState = "inactive";
        if (hasMatch) {
            visualState = "active";
        } 

        let content = <div
            className={"megamenu-entry-item " + (hasMatch ? "active" : "inactive")}
            onClick={this.renderMobilePanel}>
            {this.renderItem()}
        </div>;

        if (this.props.entry.link) {
            content = <MegaMenuPanelLink
                {...this.props}
                className={"megamenu-entry-item " + (hasMatch ? "active" : "inactive")}
                item={this.props.entry}
                showIcon />;
        }

        return <div className="megamenu-entry">
            <div className={"megamenu-entry-content " + visualState}>
                {content}

                {this.renderFavItems()}
            </div>
        </div>;
    }

    renderDesktop() {
        let popoverclass = "megamenu-popover";
        const hasMatch = this.hasItemMatch();
        if (this.props.className) {
            popoverclass = popoverclass + " popover-" + this.props.className;
        }
        let visualState = "inactive";
        if (hasMatch) {
            visualState = "active";
        } 

        if (this.props.entry.link) {
            return <div className="megamenu-entry">
                <div className={"megamenu-entry-content " + visualState}>
                    <MegaMenuPanelLink {...this.props} className="megamenu-entry-item" item={this.props.entry} showIcon />
                </div>
            </div>;
        }


        return <Tooltip
            //debug={30000}
            placement="right"
            showDelay={250}
            className="megamenu-entry"
            content={(p) => this.renderPanel(p)}
            component="div"
            openOnClick
            popoverClassName={popoverclass}
            childrenBuilder={(state) => {
                let menuVisualState = "inactive";
                if (hasMatch) {
                    menuVisualState = "active";
                } else if (state.tooltipVisible === true) {
                    menuVisualState = "tooltip-active";
                }
                // console.log("render", menuVisualState, this.props.entry, state);
                
                return <div className={"megamenu-entry-content " + menuVisualState}>
                    <div className={"megamenu-entry-item " + menuVisualState}>
                        {this.renderItem()}
                    </div>
                    
                    {this.renderFavItems()}
                </div>;
            }}            
        />;
    }

    render() {
        if (this.props.isMobile) {
            return this.renderMobile();
        }

        return this.renderDesktop();
    }
}